import type { FarmUserPermissionName } from "@/constants/permissions"
import type { AuthFormData, AuthPageId } from "@/redux/auth.reducer"

import { ENVIRONMENT } from "@/base/environment"

import type { DeviceProfilePageId } from "@/components/device-profile/device-profile-pages"

import type {
  InstallationTypeName,
  SensorName,
} from "@/base/sensor-configurations"
import type { AnySensorKey } from "@/base/sensors"
import type { AddDeviceInputMode } from "@/constants/base"

import type { AppbarPageId } from "@/components/dashboard-pages"
import type { SupportTopicId } from "@/constants/support-documentation"
import type { FieldActionName } from "@/redux/farm-fields.reducer"
import type { FormFieldId } from "@/theme/form-control/FormControlProvider"
import type { AppView } from "@/base/load-app"
import type { SelectedDeviceActionName } from "@/screens/selected-device/SelectedDeviceContext"

type NativeTabId = "deviceConnections" | "devices" | "more" | "statusMap"
export type DashboardPageId = AppbarPageId | NativeTabId

type ComponentId =
  | "background"
  | "checkbox"
  | "dialog"
  | "fieldLabel"
  | "form"
  | "formError"
  | "navMenu"
  | "pageContent"
  | "screenHeader"
  | "success"
  | "toggleSwitch"

export type ScreenId =
  | "addDevice"
  | "addPhoneNumber"
  | "addSensor"
  | "adminLanding"
  | "assistant"
  | "analyticsExternal"
  | "appNotifications"
  | "appPreferences"
  | "changeFarmAccount"
  | "contactUs"
  | "createConfiguration"
  | "createDeviceActionSchedule"
  | "createDeviceComment"
  | "createDeviceConnection"
  | "createDeviceNotificationRule"
  | "createFarm"
  | "createFarmJoinCode"
  | "createField"
  | "createSupportTicket"
  | "deactivateFarm"
  | "debugInfo"
  | "deleteAccount"
  | "deviceCommandConfirmation"
  | "deviceControlPanel"
  | "deviceSchedulesList"
  | "devicesRoster"
  | "editSensorValue"
  | "farmJoinCode"
  | "farmUsersList"
  | "fieldIrrigationHistory"
  | "fieldProfileLanding"
  | "fieldsRoster"
  | "accountDeactivated"
  | "joinFarmWithCode"
  | "loadError"
  | "manageDeviceSensor"
  | "manageNotificationsByDevice"
  | "notificationPreferences"
  | "manageUserPermissions"
  | "noFarms"
  | "notFound"
  | "passwordReset"
  | "phoneNumbersList"
  | "placeholder"
  | "quickActions"
  | "schedulesList"
  | "scratch"
  | "seedDatabase"
  | "sendTestNotificationLink"
  | "sendTestNotificationToFarmBtn"
  | "sendTestNotificationToMeBtn"
  | "setCenterPivotPath"
  | "setDeviceLocationPermanent"
  | "setLinearPath"
  | "setReelDistance"
  | "setReelRunDirection"
  | "setVfdSpeed"
  | "shareFeeback"
  | "statusMap"
  | "supportDocumentation"
  | "timeline"
  | "transferFarmOwnership"
  | "unknownFields"
  | "userEmailDisplay"
  | "userPreferences"
  | "verifyInitialPhoneNumber"
  | "verifyPhoneNumber"
  | "viewFarmUserPermissions"
  | "viewTermsOfService"
export type FeatureId =
  | AuthPageId
  | DashboardPageId
  | DeviceProfilePageId
  | FieldActionName
  | ScreenId
  | SelectedDeviceActionName

export type LinkId = `${FeatureId}Link`
type ButtonName =
  | "cancel"
  | "close"
  | "closeDialog"
  | "cycleRight"
  | "decline"
  | "delete"
  | "dislike"
  | "done"
  | "edit"
  | "focusDevice"
  | "help"
  | "like"
  | "navMenuToggle"
  | "newer"
  | "older"
  | "print"
  | "removeDeviceFromFarm"
  | "resendCode"
  | "skip"
  | "submit"

export type ButtonId = `${ButtonName}Btn`
export type TestId =
  | AddDeviceInputMode
  | AnySensorKey
  | AppView
  | ButtonId
  | ComponentId
  | DashboardPageId
  | FarmUserPermissionName
  | FeatureId
  | FormFieldId
  | InstallationTypeName
  | LinkId
  | ScreenId
  | SensorName
  | SupportTopicId
  | keyof AuthFormData
  | "deactivateFarmLink"
  | "goToAnalyticsSiteLink"
  | "leaveFarmLink"
  | "transferOwnershipLink"
  | "transferToUser"
  | "account"
  | "activeFarm"
  | "addMoreDevicesBtn"
  | "admin"
  | "adminModeSwitch"
  | "app"
  | "appBar"
  | "appIsLoading"
  | "appThemePreference"
  | "auth"
  | "azimuthSlider"
  | "azimuthText"
  | "chooseInstallationType"
  | "choosePressureSensorModel"
  | "closeFieldActionsBtn"
  | "closeSidebarBtn"
  | "confirmAccountLink"
  | "confirmDeleteNotification"
  | "confirmSendTestNotification"
  | "copyLocationButton"
  | "dashboard"
  | "dateMax"
  | "dateMin"
  | "depthIndicator"
  | "deleteNotificationBtn"
  | "deletePhoneNumberBtn"
  | "deletePhoneNumberConfirmation"
  | "deleteSensorBtn"
  | "dev"
  | "deviceActivity"
  | "deviceAnalyticsLink"
  | "deviceCycler"
  | "deviceEventHistory"
  | "deviceEventTimestamp"
  | "deviceInField"
  | "deviceLocationLink"
  | "deviceMuteToggle"
  | "deviceNotificationsToggleDenied"
  | "deviceNotificationsToggleDisabled"
  | "deviceNotificationsToggleEnabled"
  | "muteForMeToggle"
  | `deviceNotificationsListItem${number}`
  | "deviceProfile"
  | "error"
  | "experimentalFeatureWarning"
  | "farm"
  | "farmJoinCodeCopyBtn"
  | "fieldActions"
  | "fieldCycler"
  | "floatingMapCard"
  | "flowIndicator"
  | "focusReelRunBtn"
  | "googleMapsLink"
  | "gpsIndicator"
  | "hallSwitchIndicator"
  | "helpContent"
  | "input"
  | "installationTypeIndicator"
  | "instructions"
  | "accountDeactivated"
  | "internalSoc"
  | "loadError"
  | "metricBtn"
  | "newFarmSuccess"
  | "nextDeviceBtn"
  | "noListItems"
  | "notFound"
  | "notificationString"
  | "page404"
  | "page500"
  | "passwordResetLink"
  | "phoneNumber"
  | "pressureIndicator"
  | "pressureSensorYesOrNo"
  | "pressureSlider"
  | "preview"
  | "previousDeviceBtn"
  | "printTableOfContents"
  | "quickActionsBtn"
  | "rawValue"
  | "relayIndicator"
  | "renameDeviceBtn"
  | "resetDeviceBtn"
  | "reviewChoices"
  | "runEtaIndicator"
  | "runSpeedIndicator"
  | "selectedDevice"
  | "setDevicePermissions"
  | "setFarmPermissions"
  | "shareCode"
  | "showDevicesBtn"
  | "showFieldsBtn"
  | "showTimelineBtn"
  | "showMoreBtn"
  | "sidebarContent"
  | "sidebarLinks"
  | "sidebarToggleBtn"
  | "subscriptionDueReminder"
  | "subscriptionOverdueWarning"
  | "switchControlLink"
  | "unreadIndicator"
  | "usBtn"
  | "userAccountMenu"
  | "userAccountMenuToggleBtn"
  | "voltageIndicator"
  | "overrideDirectionBtn"
  | "directionFormInstructions"
  | "directionSlider"
  | "directionDisplay"
  | "flipDirectionBtn"
  | "directionFormActions"
  | "deviceLocation"
  | "humidityIndicator"
  | "overlay"
  | "editPivotBtn"
  | "addSectorBtn"
  | "saveEndGunTableBtn"
  | "endGunEditorCloseBtn"
  | "endGunTableNameInput"
  | "endGunTableActiveSwitch"
  | "pivotBuilderPanel"
  | "pivotSpeedIndicator"
  | "pivotTimeToStopIndicator"
  | "deviceNotificationStateControl"
  | "manageNotificationsByDevice"
  | "manageNotificationsByDeviceLink"
  | "managePhoneNumbersLink"
  | "notificationMapDoneBtn"
  | "notificationPreferences"
  | "notificationPreferencesLink"
  | "unmuteDeviceBtn"
  | "viewNotificationMapLink"
  | "temperatureIndicator"
  | `${DashboardPageId}Link`
  | `${SupportTopicId}Link`
  | `deviceNotificationsListItem${number}`
  | `devicesRosterItem${number}`
  | `fieldsRosterItem${number}`
  | `ioExpanderControl${number}`
  | `notificationMessageItem${number}`
  | `phoneNumbersListItem${number}`
  | `switchControl${number}`
  | `unknownFieldsListItem${number}`

export interface TestIdOptions {
  preserveId?: boolean
}
export interface TestIdProps {
  id: TestId
  preserveId?: boolean
}
export type WithTestId<T, Id = TestId> = Omit<
  T,
  "id" | "nativeID" | "testID"
> & {
  id: Id & TestId
  preserveId?: boolean
}
export interface OptionalTestId<Id = TestId> {
  id?: Id & TestId
  preserveId?: boolean
}
export type WithOptionalTestId<T, Id = TestId> = Omit<
  T,
  "id" | "nativeID" | "testID"
> &
  OptionalTestId<Id>
/**
 * Some features depend on ids being present; they are removed unless
 * we opt-in to preserve them
 */
function getShouldPreserveTestIds(options?: TestIdOptions): boolean {
  const shouldPreserve = options?.preserveId === true

  const isCypressRunning = ENVIRONMENT.isEndToEndTest

  if (isCypressRunning) {
    // Keep test ids if cypress is running
    return true
  }
  // Some ids should be preserved in production;
  // we can override the default behavior
  if (__DEV__ || shouldPreserve) {
    return true
  }
  return false
}

/**
 * Remove native and test ids in production unless we need them for something
 * @see {@link{https://reactnative.dev/docs/view#nativeid}}
 * @param id
 * @param options
 * @param options.keepInProduction
 */

export function testIds(id: TestId | undefined, options?: TestIdOptions) {
  const shouldKeep = getShouldPreserveTestIds(options)
  if (shouldKeep && typeof id === "string") {
    return { nativeID: id, testID: id }
  }
  return undefined
}
